import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Banner from "../../layouts/Banner/Banner";
import ProductCategories from "../../layouts/ProductCategories/ProductCategories";
import CategorySection from "../CategorySection/CategorySection";
import ContentBlock from "../ContentBlock/ContentBlock";
import ProductItem from "../ProductItem/ProductItem";
import Images from "../../asset/images";
import { useCategoryStore } from "../../stores/categories/useCategoryStore";
import { useLanguageStore } from "../../stores/Language/useLanguageStore";
import useCategories from "../../hooks/useCategories";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import "./SectionRenderer.scss";

const getText = (value, language = "vi") => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[language] || value.vi || value.en || "";
};

const SectionRenderer = ({ section }) => {
  if (!section || section.enabled === false) return null;

  switch (section.type) {
    case "banner":
      return <BannerSection section={section} />;
    case "product_categories":
      return <ProductCategoriesSection section={section} />;
    case "content_block":
      return <ContentBlockSection section={section} />;
    case "hero_text":
      return <HeroTextSection section={section} />;
    case "product_grid":
      return <ProductGridSection section={section} />;
    case "category_showcase":
      return <CategoryShowcaseSection section={section} />;
    case "service_cards":
      return <ServiceCardsSection section={section} />;
    case "policy_accordion":
      return <PolicyAccordionSection section={section} />;
    default:
      return null;
  }
};

const BannerSection = ({ section }) => {
  const config = section.config || {};
  return (
    <Banner
      showBtn={config.show_button !== false}
      imgSrc={config.image_url || Images.bannerImage}
      to={config.button_link || "/products/category"}
    />
  );
};

const ProductCategoriesSection = ({ section }) => {
  const { categoryJewelry } = useCategoryStore();
  const { currentLanguage } = useLanguageStore();
  const config = section.config || {};
  const items = useMemo(() => {
    const source = Array.isArray(categoryJewelry) ? categoryJewelry : [];
    return source
      .filter((item) => item.slug !== "chua-phan-loai")
      .slice(0, config.max_items || undefined)
      .map((item) => ({
        id: item.id,
        image: item.image || null,
        name: item.name,
        href: `/products/category/${capitalizeFirstLetter(item.slug)}`,
      }));
  }, [categoryJewelry, config.max_items]);

  return (
    <section className="lpb-section">
      {config.show_title !== false && (
        <h2 className="lpb-section-title">{getText(config.title, currentLanguage)}</h2>
      )}
      <div className="lpb-category-list">
        <ProductCategories items={items} showTittle={false} />
      </div>
    </section>
  );
};

// Ảnh fallback mặc định cho từng content_block section (dùng khi image_url chưa được set trong Page Builder)
const CONTENT_BLOCK_FALLBACK_IMAGES = {
  home_about1: Images.aboutSection1Image,
  home_about2: Images.giftAboutImage,
  home_about3: Images.anh3Image,
};

const ContentBlockSection = ({ section }) => {
  const { currentLanguage } = useLanguageStore();
  const config = section.config || {};
  const buttonStyle = config.button_style || (config.theme === "light" ? "secondary" : "primary");
  const buttonProps = {
    primary: buttonStyle === "primary",
    second: buttonStyle === "secondary" || buttonStyle === "second",
  };

  if (config.button_link) {
    buttonProps.to = config.button_link;
  }

  // Ưu tiên: image_url từ Page Builder → fallback theo section.id → fallback chung
  const fallbackImage = CONTENT_BLOCK_FALLBACK_IMAGES[section.id] || Images.aboutSection1Image;

  // gap_below: khoảng cách phía dưới mỗi block (mặc định 50px, tinh chỉnh được qua builder)
  const gapBelow = config.gap_below != null ? config.gap_below : 50;

  return (
    <div style={{ marginBottom: `${gapBelow}px` }}>
      <ContentBlock
        contentSide={config.content_side || "right"}
        theme={config.theme || "brown"}
        title={getText(config.title, currentLanguage)}
        description={getText(config.description, currentLanguage)}
        srcImage={config.image_url || fallbackImage}
        imageAlt={config.image_alt}
        imageFit={config.image_fit || "cover"}
        textbtn={config.show_button === false ? "" : getText(config.button_text, currentLanguage)}
        buttonProps={buttonProps}
        direction={config.direction || "vertical"}
        mobileContentLayout={config.mobile_content_layout || "title_top_button_bottom"}
      />
    </div>
  );
};

const HeroTextSection = ({ section }) => {
  const { currentLanguage } = useLanguageStore();
  const config = section.config || {};
  const paragraphs = Array.isArray(config.paragraphs) ? config.paragraphs : [];
  return (
    <section className="lpb-hero-text">
      <img
        src={config.background_image_url || Images.aboutUsImage}
        alt={config.background_image_alt || getText(config.title, currentLanguage)}
        style={{ objectFit: config.background_image_fit || "cover" }}
      />
      <div className="lpb-hero-text__content">
        <h1>{getText(config.title, currentLanguage)}</h1>
        {paragraphs.map((item, index) => (
          <p key={index}>{getText(item.text, currentLanguage)}</p>
        ))}
      </div>
    </section>
  );
};

const ProductGridSection = ({ section }) => {
  const { categoryGifts, categoryJewelry } = useCategoryStore();
  const { currentLanguage } = useLanguageStore();
  const config = section.config || {};
  const source = config.data_source === "gifts_category" ? categoryGifts : categoryJewelry;
  const products = Array.isArray(source) ? source : [];

  return (
    <section className="lpb-section">
      <h1 className="lpb-section-title">{getText(config.title, currentLanguage)}</h1>
      <div className="lpb-product-grid">
        {products.map((item) => (
          <ProductItem
            key={item.id}
            srcImage={item.image?.src || item.image}
            productText={item.name}
            textType="title"
            href={`/products/category/${capitalizeFirstLetter(item.slug)}`}
            isShowHeart={false}
          />
        ))}
      </div>
    </section>
  );
};

const CategoryShowcaseSection = ({ section }) => {
  const config = section.config || {};
  const { categories, loading } = useCategories(config.per_category_count || 4);

  if (loading) return <p className="lpb-loading">Loading categories...</p>;

  return (
    <section className="lpb-section">
      {(categories || []).map((category) => (
        <CategorySection key={category.id} category={category} />
      ))}
    </section>
  );
};

const ServiceCardsSection = ({ section }) => {
  const { currentLanguage } = useLanguageStore();
  const config = section.config || {};
  const cards = Array.isArray(config.cards) ? config.cards : [];

  return (
    <section className="lpb-section lpb-service-cards">
      <header>
        <h1>{getText(config.header_title, currentLanguage)}</h1>
        <p>{getText(config.header_subtitle, currentLanguage)}</p>
      </header>
      <div className="lpb-card-grid">
        {cards.map((card, index) => (
          <article className="lpb-card" key={index}>
            <h2>{getText(card.title, currentLanguage)}</h2>
            <p>{getText(card.description || card.intro, currentLanguage)}</p>
            {card.link_url && (
              <Link to={card.link_url}>{getText(card.link_text, currentLanguage) || "Xem thêm"}</Link>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

const PolicyAccordionSection = ({ section }) => {
  const { currentLanguage } = useLanguageStore();
  const config = section.config || {};
  const policies = Array.isArray(config.policies) ? config.policies : [];
  const [open, setOpen] = useState({});

  return (
    <section className="lpb-section lpb-policy">
      <h1>{getText(config.header_title, currentLanguage)}</h1>
      <p>{getText(config.intro_text, currentLanguage)}</p>
      {policies.map((policy, index) => {
        const key = policy.key || index;
        return (
          <article className="lpb-policy-item" key={key}>
            <button type="button" onClick={() => setOpen((prev) => ({ ...prev, [key]: !prev[key] }))}>
              {getText(policy.title, currentLanguage)}
            </button>
            {open[key] && (
              <div>
                <p>{getText(policy.intro, currentLanguage)}</p>
                {(policy.items || []).map((item, itemIndex) => (
                  <p key={itemIndex}>
                    <strong>{getText(item.title, currentLanguage)}</strong> {getText(item.content, currentLanguage)}
                  </p>
                ))}
              </div>
            )}
          </article>
        );
      })}
    </section>
  );
};

export default SectionRenderer;
